import React, { useState, useEffect } from "react";
import { vendorApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  FiTruck,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiMail,
  FiPhone,
  FiMapPin,
  FiFileText,
  FiList,
  FiArrowLeft,
  FiCheckCircle,
  FiUser,
  FiRotateCcw,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiRefreshCw,
} from "react-icons/fi";
import { toast } from "react-toastify";
import Modal from "../components/Modal";

const VendorsPage = () => {
  const { isAdmin } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Backend Search & Pagination states
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  // View state: 'list' (Directory table) | 'form' (Add or Edit Vendor page)
  const [activeTab, setActiveTab] = useState("list");
  const [editingVendor, setEditingVendor] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, vendor: null });

  const initialForm = {
    company_name: "",
    email: "",
    contact: "",
    address: "",
    contact_person_name: "",
    ntn: "",
    status: "active",
  };
  const [formData, setFormData] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  // Debounce search input by 350ms to query backend efficiently
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchVendors = async (currentPage = page, currentLimit = pageSize, currentSearch = debouncedSearch) => {
    try {
      setLoading(true);
      const res = await vendorApi.getAll({
        page: currentPage,
        limit: currentLimit,
        search: currentSearch.trim() || undefined,
      });
      setVendors(res.data || []);
      if (res.pagination) {
        setPagination(res.pagination);
      } else {
        const total = (res.data || []).length;
        setPagination({
          total,
          page: currentPage,
          limit: currentLimit,
          totalPages: Math.ceil(total / currentLimit) || 1,
        });
      }
    } catch (err) {
      toast.error(err.message || "Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors(page, pageSize, debouncedSearch);
  }, [page, pageSize, debouncedSearch]);

  const handleStartAdd = () => {
    setEditingVendor(null);
    setFormData(initialForm);
    setActiveTab("form");
  };

  const handleStartEdit = (v) => {
    setEditingVendor(v);
    setFormData({
      company_name: v.company_name || "",
      email: v.email || "",
      contact: v.contact || "",
      address: v.address || "",
      contact_person_name: v.contact_person_name || "",
      ntn: v.ntn || "",
      status: v.status || "active",
    });
    setActiveTab("form");
  };

  const handleBackToList = () => {
    setActiveTab("list");
    setEditingVendor(null);
  };

  const handleSaveVendor = async (e) => {
    e.preventDefault();
    if (!formData.company_name.trim()) {
      toast.warning("Company name is required");
      return;
    }

    try {
      setSubmitting(true);
      if (editingVendor) {
        await vendorApi.update(editingVendor.id, formData);
        toast.success("Vendor updated successfully");
      } else {
        await vendorApi.create(formData);
        toast.success("Vendor added successfully");
      }
      setActiveTab("list");
      setEditingVendor(null);
      fetchVendors(page, pageSize, debouncedSearch);
    } catch (err) {
      toast.error(err.message || "Failed to save vendor");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVendor = async () => {
    if (!deleteModal.vendor) return;
    try {
      await vendorApi.delete(deleteModal.vendor.id);
      toast.success("Vendor deleted successfully");
      setDeleteModal({ open: false, vendor: null });
      const newPage = vendors.length === 1 && page > 1 ? page - 1 : page;
      setPage(newPage);
      fetchVendors(newPage, pageSize, debouncedSearch);
    } catch (err) {
      toast.error(err.message || "Failed to delete vendor");
    }
  };

  return (
    <div className="fade-in">
      {/* Top Header & Page Navigation Tabs */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div>
          <h2 style={{ fontSize: "1.45rem", fontWeight: 800, color: "var(--navy)" }}>
            Vendor Management <span style={{ fontSize: "0.85rem", color: "var(--accent)", fontWeight: 700 }}>Suppliers</span>
          </h2>

        </div>

        {/* View Mode Toggle: Directory vs Add/Edit on Page */}
        <div
          style={{
            display: "flex",
            backgroundColor: "#fff",
            padding: 4,
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            gap: 4,
          }}
        >
          <button
            onClick={() => {
              setActiveTab("list");
              setEditingVendor(null);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 18px",
              borderRadius: "var(--radius-sm)",
              border: "none",
              backgroundColor: activeTab === "list" ? "var(--primary)" : "transparent",
              color: activeTab === "list" ? "#fff" : "var(--slate-600)",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            <FiList size={16} />
            Vendor Directory
          </button>

          <button
            onClick={handleStartAdd}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 18px",
              borderRadius: "var(--radius-sm)",
              border: "none",
              backgroundColor: activeTab === "form" ? "var(--primary)" : "transparent",
              color: activeTab === "form" ? "#fff" : "var(--slate-600)",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            {editingVendor ? <FiEdit2 size={16} /> : <FiPlus size={16} />}
            {editingVendor ? `Edit: ${editingVendor.company_name}` : "Add Vendor"}
          </button>
        </div>
      </div>

      {/* ===================== VIEW 1: FORM ON PAGE ===================== */}
      {activeTab === "form" && (
        <div className="fade-in">
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              overflow: "hidden",
              marginBottom: 30,
            }}
          >
            {/* Form Top Title Bar */}
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid var(--slate-100)",
                backgroundColor: "var(--slate-50)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: "var(--radius-md)",
                    backgroundColor: "var(--primary-light)",
                    color: "var(--primary)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FiTruck size={22} />
                </div>
                <div>
                  <h3 style={{ fontSize: "1.18rem", fontWeight: 800, color: "var(--navy)", margin: 0 }}>
                    {editingVendor ? `Edit Vendor: ${editingVendor.company_name}` : "Add New Vendor Supplier"}
                  </h3>
                  <p style={{ fontSize: "0.82rem", color: "var(--slate-500)", margin: "3px 0 0 0" }}>
                    {editingVendor
                      ? "Update supplier contact information, representative details, or tax numbers."
                      : "Fill in the company details to register a new supplier in the system."}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleBackToList}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 14px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--slate-300)",
                  backgroundColor: "#fff",
                  color: "var(--slate-700)",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <FiArrowLeft size={16} /> Back to Directory
              </button>
            </div>

            {/* Form Inputs Container */}
            <form onSubmit={handleSaveVendor} style={{ padding: "26px 28px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                  gap: 24,
                  marginBottom: 24,
                }}
              >
                {/* Section 1: Company Profile */}
                <div
                  style={{
                    backgroundColor: "var(--slate-50)",
                    border: "1px solid var(--slate-200)",
                    borderRadius: "var(--radius-md)",
                    padding: 20,
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.86rem",
                      fontWeight: 700,
                      color: "var(--primary)",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 16,
                      borderBottom: "1px solid var(--slate-200)",
                      paddingBottom: 8,
                    }}
                  >
                    <FiTruck size={16} /> 1. Company Profile
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: "var(--slate-700)",
                        marginBottom: 6,
                      }}
                    >
                      Company / Supplier Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Dell Technologies Inc. or TechSupplies Ltd."
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--slate-300)",
                        fontSize: "0.9rem",
                        backgroundColor: "#fff",
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: "var(--slate-700)",
                        marginBottom: 6,
                      }}
                    >
                      Contact Person / Representative
                    </label>
                    <div style={{ position: "relative" }}>
                      <FiUser
                        size={16}
                        style={{
                          position: "absolute",
                          left: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "var(--slate-400)",
                        }}
                      />
                      <input
                        type="text"
                        placeholder="e.g. Sarah Jenkins (Account Representative)"
                        value={formData.contact_person_name}
                        onChange={(e) => setFormData({ ...formData, contact_person_name: e.target.value })}
                        style={{
                          width: "100%",
                          padding: "10px 14px 10px 38px",
                          borderRadius: "var(--radius-md)",
                          border: "1px solid var(--slate-300)",
                          fontSize: "0.9rem",
                          backgroundColor: "#fff",
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: "var(--slate-700)",
                        marginBottom: 6,
                      }}
                    >
                      Account Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--slate-300)",
                        fontSize: "0.9rem",
                        backgroundColor: "#fff",
                        fontWeight: 600,
                      }}
                    >
                      <option value="active">Active Supplier</option>
                      <option value="inactive">Inactive / Archived</option>
                    </select>
                  </div>
                </div>

                {/* Section 2: Contact & Tax Information */}
                <div
                  style={{
                    backgroundColor: "var(--slate-50)",
                    border: "1px solid var(--slate-200)",
                    borderRadius: "var(--radius-md)",
                    padding: 20,
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.86rem",
                      fontWeight: 700,
                      color: "var(--primary)",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 16,
                      borderBottom: "1px solid var(--slate-200)",
                      paddingBottom: 8,
                    }}
                  >
                    <FiMail size={16} /> 2. Communication & Tax Identification
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.82rem",
                          fontWeight: 700,
                          color: "var(--slate-700)",
                          marginBottom: 6,
                        }}
                      >
                        Email Address
                      </label>
                      <div style={{ position: "relative" }}>
                        <FiMail
                          size={15}
                          style={{
                            position: "absolute",
                            left: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "var(--slate-400)",
                          }}
                        />
                        <input
                          type="email"
                          placeholder="sales@vendor.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          style={{
                            width: "100%",
                            padding: "10px 12px 10px 36px",
                            borderRadius: "var(--radius-md)",
                            border: "1px solid var(--slate-300)",
                            fontSize: "0.9rem",
                            backgroundColor: "#fff",
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.82rem",
                          fontWeight: 700,
                          color: "var(--slate-700)",
                          marginBottom: 6,
                        }}
                      >
                        Contact Number
                      </label>
                      <div style={{ position: "relative" }}>
                        <FiPhone
                          size={15}
                          style={{
                            position: "absolute",
                            left: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "var(--slate-400)",
                          }}
                        />
                        <input
                          type="text"
                          placeholder="+92 300 1234567"
                          value={formData.contact}
                          onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                          style={{
                            width: "100%",
                            padding: "10px 12px 10px 36px",
                            borderRadius: "var(--radius-md)",
                            border: "1px solid var(--slate-300)",
                            fontSize: "0.9rem",
                            backgroundColor: "#fff",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: "var(--slate-700)",
                        marginBottom: 6,
                      }}
                    >
                      NTN (National Tax Number)
                    </label>
                    <div style={{ position: "relative" }}>
                      <FiFileText
                        size={15}
                        style={{
                          position: "absolute",
                          left: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "var(--slate-400)",
                        }}
                      />
                      <input
                        type="text"
                        placeholder="e.g. 1234567-8"
                        value={formData.ntn}
                        onChange={(e) => setFormData({ ...formData, ntn: e.target.value })}
                        style={{
                          width: "100%",
                          padding: "10px 12px 10px 36px",
                          borderRadius: "var(--radius-md)",
                          border: "1px solid var(--slate-300)",
                          fontSize: "0.9rem",
                          backgroundColor: "#fff",
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: "var(--slate-700)",
                        marginBottom: 6,
                      }}
                    >
                      Business / Warehouse Physical Address
                    </label>
                    <textarea
                      rows={2}
                      placeholder="Office floor, building, street address, city..."
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--slate-300)",
                        fontSize: "0.9rem",
                        resize: "vertical",
                        backgroundColor: "#fff",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions Footer */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                  paddingTop: 18,
                  borderTop: "1px solid var(--slate-200)",
                }}
              >
                <button
                  type="button"
                  onClick={() => setFormData(initialForm)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "10px 18px",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--slate-300)",
                    backgroundColor: "#fff",
                    color: "var(--slate-600)",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.88rem",
                  }}
                >
                  <FiRotateCcw size={15} /> Reset Form
                </button>

                <div style={{ display: "flex", gap: 12 }}>
                  <button
                    type="button"
                    onClick={handleBackToList}
                    style={{
                      padding: "10px 20px",
                      borderRadius: "var(--radius-md)",
                      border: "1px solid var(--slate-300)",
                      backgroundColor: "#fff",
                      color: "var(--slate-700)",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: "0.88rem",
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 26px",
                      borderRadius: "var(--radius-md)",
                      border: "none",
                      backgroundColor: "var(--primary)",
                      color: "#fff",
                      cursor: submitting ? "not-allowed" : "pointer",
                      fontWeight: 700,
                      fontSize: "0.92rem",
                      boxShadow: "var(--shadow-md)",
                    }}
                  >
                    <FiCheckCircle size={17} />
                    {submitting ? "Saving..." : editingVendor ? "Update Vendor Profile" : "Save Vendor Supplier"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===================== VIEW 2: DIRECTORY TABLE ===================== */}
      {activeTab === "list" && (
        <div className="fade-in">
          {/* Filter / Search Bar */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              padding: "16px 20px",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              marginBottom: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <div style={{ position: "relative", flex: 1, minWidth: 260, maxWidth: 460 }}>
              <FiSearch
                size={18}
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--slate-400)",
                }}
              />
              <input
                type="text"
                placeholder="Search by vendor name, representative, NTN, phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 36px 9px 38px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--slate-300)",
                  fontSize: "0.88rem",
                  outline: "none",
                }}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--slate-400)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 2,
                  }}
                  title="Clear search"
                >
                  <FiX size={15} />
                </button>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button
                type="button"
                onClick={() => fetchVendors(page, pageSize, debouncedSearch)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 12px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--slate-200)",
                  backgroundColor: "#fff",
                  color: "var(--slate-700)",
                  cursor: "pointer",
                  fontSize: "0.84rem",
                  fontWeight: 600,
                }}
                title="Refresh list"
              >
                <FiRefreshCw size={14} className={loading ? "spin" : ""} /> Refresh
              </button>
              <div style={{ fontSize: "0.85rem", color: "var(--slate-500)", fontWeight: 600 }}>
                Total Registered: <strong style={{ color: "var(--navy)" }}>{pagination.total}</strong>
              </div>
              <button
                onClick={handleStartAdd}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  backgroundColor: "var(--primary)",
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: "var(--radius-md)",
                  border: "none",
                  fontWeight: 700,
                  fontSize: "0.84rem",
                  cursor: "pointer",
                }}
              >
                <FiPlus size={16} /> Add Vendor
              </button>
            </div>
          </div>

          {/* Vendors Table */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              overflow: "hidden",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
                <thead>
                  <tr
                    style={{
                      backgroundColor: "var(--slate-50)",
                      borderBottom: "1px solid var(--slate-200)",
                      textAlign: "left",
                    }}
                  >
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Company Name</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Contact Person</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Email & Phone</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Address</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>NTN #</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} style={{ padding: 40, textAlign: "center", color: "var(--slate-500)" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                          <FiRefreshCw size={18} className="spin" style={{ color: "var(--primary)" }} />
                          <span>Loading vendors from server...</span>
                        </div>
                      </td>
                    </tr>
                  ) : vendors.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ padding: 40, textAlign: "center", color: "var(--slate-400)" }}>
                        <div style={{ marginBottom: 10 }}>
                          {debouncedSearch
                            ? `No vendors found matching "${debouncedSearch}".`
                            : "No vendors registered in the system yet."}
                        </div>
                        {debouncedSearch ? (
                          <button
                            onClick={() => setSearch("")}
                            style={{
                              padding: "6px 14px",
                              backgroundColor: "var(--slate-100)",
                              color: "var(--slate-700)",
                              border: "1px solid var(--slate-300)",
                              borderRadius: "var(--radius-md)",
                              fontWeight: 600,
                              fontSize: "0.82rem",
                              cursor: "pointer",
                            }}
                          >
                            Clear Search
                          </button>
                        ) : (
                          <button
                            onClick={handleStartAdd}
                            style={{
                              padding: "8px 16px",
                              backgroundColor: "var(--primary-light)",
                              color: "var(--primary)",
                              border: "none",
                              borderRadius: "var(--radius-md)",
                              fontWeight: 700,
                              fontSize: "0.85rem",
                              cursor: "pointer",
                            }}
                          >
                            + Create First Vendor
                          </button>
                        )}
                      </td>
                    </tr>
                  ) : (
                    vendors.map((v) => (
                      <tr
                        key={v.id}
                        style={{
                          borderBottom: "1px solid var(--slate-100)",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-50)")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <td style={{ padding: "14px 18px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: 8,
                                backgroundColor: "var(--primary-light)",
                                color: "var(--primary)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <FiTruck size={17} />
                            </div>
                            <div>
                              <span style={{ fontWeight: 700, color: "var(--navy)", display: "block" }}>{v.company_name}</span>
                              {v.status === "inactive" && (
                                <span
                                  style={{
                                    fontSize: "0.72rem",
                                    color: "var(--slate-500)",
                                    backgroundColor: "var(--slate-100)",
                                    padding: "1px 6px",
                                    borderRadius: 4,
                                  }}
                                >
                                  Inactive
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        <td style={{ padding: "14px 18px", fontWeight: 600, color: "var(--slate-800)" }}>
                          {v.contact_person_name || <span style={{ color: "var(--slate-400)" }}>—</span>}
                        </td>

                        <td style={{ padding: "14px 18px" }}>
                          {v.email && (
                            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--slate-700)" }}>
                              <FiMail size={13} color="var(--slate-400)" />
                              <span>{v.email}</span>
                            </div>
                          )}
                          {v.contact && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                color: "var(--slate-500)",
                                fontSize: "0.8rem",
                                marginTop: 2,
                              }}
                            >
                              <FiPhone size={13} color="var(--slate-400)" />
                              <span>{v.contact}</span>
                            </div>
                          )}
                          {!v.email && !v.contact && <span style={{ color: "var(--slate-400)" }}>—</span>}
                        </td>

                        <td style={{ padding: "14px 18px", color: "var(--slate-600)", maxWidth: 220 }}>
                          {v.address ? (
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                              <FiMapPin size={13} color="var(--slate-400)" style={{ marginTop: 3, flexShrink: 0 }} />
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {v.address}
                              </span>
                            </div>
                          ) : (
                            <span style={{ color: "var(--slate-400)" }}>—</span>
                          )}
                        </td>

                        <td style={{ padding: "14px 18px" }}>
                          {v.ntn ? (
                            <span
                              style={{
                                fontFamily: "monospace",
                                fontWeight: 700,
                                padding: "2px 8px",
                                borderRadius: 4,
                                backgroundColor: "var(--slate-100)",
                                color: "var(--slate-800)",
                                fontSize: "0.8rem",
                              }}
                            >
                              {v.ntn}
                            </span>
                          ) : (
                            <span style={{ color: "var(--slate-400)" }}>—</span>
                          )}
                        </td>

                        <td style={{ padding: "14px 18px", textAlign: "right" }}>
                          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                            <button
                              onClick={() => handleStartEdit(v)}
                              style={{
                                padding: "6px 12px",
                                borderRadius: 6,
                                border: "1px solid var(--slate-200)",
                                backgroundColor: "#fff",
                                color: "var(--slate-700)",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                fontSize: "0.82rem",
                                fontWeight: 600,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "var(--primary-light)";
                                e.currentTarget.style.color = "var(--primary)";
                                e.currentTarget.style.borderColor = "var(--primary)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "#fff";
                                e.currentTarget.style.color = "var(--slate-700)";
                                e.currentTarget.style.borderColor = "var(--slate-200)";
                              }}
                            >
                              <FiEdit2 size={14} /> Edit
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => setDeleteModal({ open: true, vendor: v })}
                                style={{
                                  padding: "6px 10px",
                                  borderRadius: 6,
                                  border: "1px solid #FECACA",
                                  backgroundColor: "#FEF2F2",
                                  color: "var(--danger)",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  fontSize: "0.8rem",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "var(--danger)";
                                  e.currentTarget.style.color = "#fff";
                                  e.currentTarget.style.borderColor = "var(--danger)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "#FEF2F2";
                                  e.currentTarget.style.color = "var(--danger)";
                                  e.currentTarget.style.borderColor = "#FECACA";
                                }}
                              >
                                <FiTrash2 size={14} /> Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {pagination.total > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                  padding: "14px 20px",
                  borderTop: "1px solid var(--slate-200)",
                  backgroundColor: "var(--slate-50)",
                  fontSize: "0.84rem",
                  color: "var(--slate-600)",
                }}
              >
                <div>
                  Showing <strong>{Math.min((page - 1) * pageSize + 1, pagination.total)}</strong> to{" "}
                  <strong>{Math.min(page * pageSize, pagination.total)}</strong> of{" "}
                  <strong>{pagination.total}</strong> {pagination.total === 1 ? "supplier" : "suppliers"}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  {/* Rows Per Page Selector */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: "0.82rem", fontWeight: 600 }}>Rows:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        const newLimit = Number(e.target.value);
                        setPageSize(newLimit);
                        setPage(1);
                      }}
                      style={{
                        padding: "4px 8px",
                        borderRadius: 6,
                        border: "1px solid var(--slate-300)",
                        fontSize: "0.82rem",
                        backgroundColor: "#fff",
                        fontWeight: 600,
                        color: "var(--navy)",
                        cursor: "pointer",
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  {/* Prev Button */}
                  <button
                    type="button"
                    disabled={page === 1 || loading}
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "5px 12px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      backgroundColor: page === 1 ? "var(--slate-100)" : "#fff",
                      color: page === 1 ? "var(--slate-400)" : "var(--slate-700)",
                      cursor: page === 1 || loading ? "not-allowed" : "pointer",
                      fontWeight: 600,
                      fontSize: "0.82rem",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <FiChevronLeft size={15} /> Prev
                  </button>

                  {/* Numbered Page Buttons */}
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                      .filter((p) => {
                        if (pagination.totalPages <= 7) return true;
                        if (p === 1 || p === pagination.totalPages) return true;
                        if (Math.abs(p - page) <= 1) return true;
                        return false;
                      })
                      .map((p, idx, arr) => {
                        const prev = arr[idx - 1];
                        const showEllipsis = prev && p - prev > 1;

                        return (
                          <React.Fragment key={p}>
                            {showEllipsis && (
                              <span style={{ padding: "0 4px", color: "var(--slate-400)" }}>...</span>
                            )}
                            <button
                              type="button"
                              disabled={loading}
                              onClick={() => setPage(p)}
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: 6,
                                border: p === page ? "1.5px solid var(--primary)" : "1px solid var(--slate-300)",
                                backgroundColor: p === page ? "var(--primary)" : "#fff",
                                color: p === page ? "#fff" : "var(--slate-700)",
                                fontWeight: 700,
                                fontSize: "0.8rem",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                              }}
                            >
                              {p}
                            </button>
                          </React.Fragment>
                        );
                      })}
                  </div>

                  {/* Next Button */}
                  <button
                    type="button"
                    disabled={page >= pagination.totalPages || loading}
                    onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "5px 12px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      backgroundColor: page >= pagination.totalPages ? "var(--slate-100)" : "#fff",
                      color: page >= pagination.totalPages ? "var(--slate-400)" : "var(--slate-700)",
                      cursor: page >= pagination.totalPages || loading ? "not-allowed" : "pointer",
                      fontWeight: 600,
                      fontSize: "0.82rem",
                      transition: "all 0.15s ease",
                    }}
                  >
                    Next <FiChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal.open} onClose={() => setDeleteModal({ open: false, vendor: null })} maxWidth={400}>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: "var(--danger-bg)",
              color: "var(--danger)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <FiTrash2 size={24} />
          </div>
          <h4 style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>
            Delete Vendor
          </h4>
          <p style={{ fontSize: "0.85rem", color: "var(--slate-500)", marginBottom: 20 }}>
            Are you sure you want to delete vendor <strong>"{deleteModal.vendor?.company_name}"</strong>?
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
            <button
              type="button"
              onClick={() => setDeleteModal({ open: false, vendor: null })}
              style={{
                padding: "8px 18px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--slate-300)",
                backgroundColor: "#fff",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteVendor}
              style={{
                padding: "8px 20px",
                borderRadius: "var(--radius-md)",
                border: "none",
                backgroundColor: "var(--danger)",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Delete Vendor
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default VendorsPage;
